import { useState } from 'react';
import type React from 'react';
import clsx from 'clsx';
import { InsightsSectionNav } from '../../weld-analytics/components/shared/InsightsSectionNav';

interface Insight {
  type: string;
  severity?: 'critical' | 'high' | 'medium' | 'low' | string;
  confidence?: number;
  title: string;
  description: string;
  affectedRecords?: number;
  details?: Record<string, any>;
  recommendation?: string;
  priority?: string;
}

interface InsightSummary {
  totalRecords: number;
  rejectionRate: number | null;
  acceptedCount: number;
  rejectedCount: number;
  hasMixedData?: boolean;
  primaryDefectType: string;
  primaryDefectPercentage: number;
  keyFindings: {
    highSeverityPatterns: number;
    highSeverityCorrelations: number;
    criticalAnomalies: number;
  };
  overallRiskLevel: 'critical' | 'high' | 'medium' | 'low';
  naturalLanguageSummary: string;
}

interface InsightsData {
  summary: InsightSummary;
  patterns: Insight[];
  correlations: Insight[];
  anomalies: Insight[];
  riskScores: any;
  rootCauseAnalysis: {
    commonThemes?: any[];
    rcaFrequency?: any;
    insights?: Insight[];
    rcaPatterns?: any[];
    structuredFactors?: any[];
    rootCauseChains?: any[];
    defectRootCauseCorrelations?: any[];
  };
  temporalTrends: Insight[];
  welderPerformance: any;
  comparativeAnalysis: Insight[];
  recommendations: Insight[];
}

interface InsightsPanelProps {
  insights: InsightsData | null;
  isLoading?: boolean;
  /** Hide the section pill nav (parent owns a unified nav). */
  hideNav?: boolean;
  /** Controlled active section id. */
  activeSection?: string;
  onActiveSectionChange?: (id: string) => void;
}

const SeverityBadge = ({ severity }: { severity?: string | null }) => {
  const colors = {
    critical: 'text-readable-red bg-[rgba(196,0,31,0.06)]',
    high: 'text-[#B38600] bg-[rgba(230,161,0,0.08)]',
    medium: 'text-[#B38600] bg-[rgba(230,161,0,0.08)]',
    low: 'text-blueprint-blue bg-[rgba(0,39,63,0.06)]',
  };

  const severityValue = severity || 'low';
  const severityKey = severityValue.toLowerCase() as keyof typeof colors;

  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium',
        colors[severityKey] || colors.low
      )}
    >
      {severityValue.toUpperCase()}
    </span>
  );
};

const InsightCard = ({ insight, index }: { insight: Insight; index: number }) => {
  const [expanded, setExpanded] = useState(index < 3); // Expand first 3 by default

  return (
    <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
      <div
        className="cursor-pointer p-4 hover:bg-slate-50"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              {(insight.severity || insight.priority) && (
                <SeverityBadge severity={insight.severity || insight.priority} />
              )}
              {insight.confidence && (
                <span className="text-xs text-slate-500">
                  {Math.round(insight.confidence * 100)}% confidence
                </span>
              )}
            </div>
            <h3 className="text-sm font-semibold text-slate-900">{insight.title}</h3>
            <p className="mt-1 text-sm text-slate-600">{insight.description}</p>
            {insight.affectedRecords && (
              <p className="mt-1 text-xs text-slate-500">
                Affects {insight.affectedRecords} record{insight.affectedRecords !== 1 ? 's' : ''}
              </p>
            )}
          </div>
          <button className="ml-4 text-slate-400 hover:text-slate-600">
            {expanded ? '−' : '+'}
          </button>
        </div>
      </div>
      {expanded && (
        <div className="border-t border-slate-200 bg-slate-50 p-4">
          {insight.recommendation && (
            <div className="mb-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                Recommendation
              </p>
              <p className="text-sm text-slate-700">{insight.recommendation}</p>
            </div>
          )}
          {insight.details && Object.keys(insight.details).length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                Details
              </p>
              <dl className="grid grid-cols-2 gap-2 text-sm">
                {Object.entries(insight.details).map(([key, value]) => (
                  <div key={key}>
                    <dt className="text-slate-500">{key.replace(/([A-Z])/g, ' $1').trim()}:</dt>
                    <dd className="font-medium text-slate-900">{String(value)}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const SectionHeader = ({ title, count, description }: { title: string; count?: number; description?: string }) => (
  <div className="mb-4">
    <div className="flex items-center gap-3">
      <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
      {count !== undefined && (
        <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700">
          {count}
        </span>
      )}
    </div>
    {description && <p className="mt-1 text-sm text-slate-600">{description}</p>}
  </div>
);

export const InsightsPanel = ({
  insights,
  isLoading,
  hideNav = false,
  activeSection: activeSectionProp,
  onActiveSectionChange,
}: InsightsPanelProps) => {
  const [internalSection, setInternalSection] = useState<string>('summary');
  const activeSection = activeSectionProp ?? internalSection;
  const setActiveSection = onActiveSectionChange ?? setInternalSection;
  const [expandedWelderSections, setExpandedWelderSections] = useState<Set<string>>(new Set(['highRisk']));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-brand border-r-transparent"></div>
          <p className="mt-4 text-sm text-slate-500">Generating insights...</p>
        </div>
      </div>
    );
  }

  if (!insights) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-10 text-center text-slate-500">
        <p className="text-lg font-semibold text-slate-900">No Insights Available</p>
        <p className="mt-2 text-sm text-slate-500">
          Upload data to generate AI-powered insights and recommendations.
        </p>
      </div>
    );
  }

  const welderCount = 
    (insights.welderPerformance?.highRiskWelders?.length || 0) +
    (insights.welderPerformance?.consistentPerformers?.length || 0) +
    (insights.welderPerformance?.welderDefectPatterns?.length || 0) +
    (insights.welderPerformance?.welderCombinations?.length || 0);

  const sections = [
    { id: 'summary', label: 'Executive Summary', count: null },
    { id: 'patterns', label: 'Patterns', count: insights.patterns?.length || 0 },
    { id: 'correlations', label: 'Correlations', count: insights.correlations?.length || 0 },
    { id: 'anomalies', label: 'Anomalies', count: insights.anomalies?.length || 0 },
    { id: 'temporal', label: 'Temporal Trends', count: insights.temporalTrends?.length || 0 },
    { id: 'welder', label: 'Welder Performance', count: welderCount > 0 ? welderCount : null },
    { id: 'rootcause', label: 'Root Cause Analysis', count: insights.rootCauseAnalysis?.insights?.length || 0 },
    { id: 'comparative', label: 'Comparative Analysis', count: insights.comparativeAnalysis?.length || 0 },
    { id: 'recommendations', label: 'Recommendations', count: insights.recommendations?.length || 0 },
  ];

  const renderSectionContent = () => {
    switch (activeSection) {
      case 'summary':
        return (
          <div className="space-y-6">
            {/* Risk Level Card */}
            <div className="rounded-lg border-2 border-slate-200 bg-white p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900">Overall Risk Assessment</h3>
                <SeverityBadge severity={insights.summary.overallRiskLevel} />
              </div>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                <div>
                  <p className="text-xs text-slate-500">Rejection Rate</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {insights.summary.rejectionRate !== null 
                      ? `${insights.summary.rejectionRate.toFixed(1)}%`
                      : 'N/A'}
                  </p>
                  {!insights.summary.hasMixedData && (
                    <p className="text-xs text-slate-400 mt-1">Defect-only dataset</p>
                  )}
                </div>
                <div>
                  <p className="text-xs text-slate-500">Total Records</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {insights.summary.totalRecords.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Accepted</p>
                  <p className="text-2xl font-bold text-emerald-600">
                    {insights.summary.acceptedCount.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Rejected</p>
                  <p className="text-2xl font-bold text-red-600">
                    {insights.summary.rejectedCount.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            {/* Natural Language Summary */}
            <div className="rounded-lg border border-slate-200 bg-blue-50 p-6">
              <h3 className="mb-3 text-sm font-semibold text-slate-900">Executive Summary</h3>
              <p className="text-sm leading-relaxed text-slate-700">
                {insights.summary.naturalLanguageSummary}
              </p>
            </div>

            {/* Key Findings */}
            <div className="rounded-lg border border-slate-200 bg-white p-6">
              <h3 className="mb-4 text-sm font-semibold text-slate-900">Key Findings</h3>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="rounded-lg bg-slate-50 p-4">
                  <p className="text-xs text-slate-500">High Severity Patterns</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {insights.summary.keyFindings.highSeverityPatterns}
                  </p>
                </div>
                <div className="rounded-lg bg-slate-50 p-4">
                  <p className="text-xs text-slate-500">Strong Correlations</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {insights.summary.keyFindings.highSeverityCorrelations}
                  </p>
                </div>
                <div className="rounded-lg bg-slate-50 p-4">
                  <p className="text-xs text-slate-500">Critical Anomalies</p>
                  <p className="text-2xl font-bold text-red-600">
                    {insights.summary.keyFindings.criticalAnomalies}
                  </p>
                </div>
              </div>
            </div>

            {/* Primary Defect Type */}
            <div className="rounded-lg border border-slate-200 bg-white p-6">
              <h3 className="mb-3 text-sm font-semibold text-slate-900">Primary Defect Type</h3>
              <div className="flex items-center justify-between">
                <p className="text-lg font-medium text-slate-900">
                  {insights.summary.primaryDefectType}
                </p>
                <p className="text-2xl font-bold text-slate-900">
                  {insights.summary.primaryDefectPercentage.toFixed(1)}%
                </p>
              </div>
              <p className="mt-2 text-xs text-slate-500">
                of all rejected welds
              </p>
            </div>
          </div>
        );

      case 'patterns':
        return (
          <div className="space-y-4">
            <SectionHeader
              title="Detected Patterns"
              count={insights.patterns?.length || 0}
              description="Multi-dimensional patterns identified across pipe specifications, positions, shifts, and welders"
            />
            {insights.patterns && insights.patterns.length > 0 ? (
              insights.patterns.map((pattern, idx) => (
                <InsightCard key={idx} insight={pattern} index={idx} />
              ))
            ) : (
              <p className="text-sm text-slate-500">No patterns detected.</p>
            )}
          </div>
        );

      case 'correlations':
        return (
          <div className="space-y-4">
            <SectionHeader
              title="Correlations"
              count={insights.correlations?.length || 0}
              description="Strong relationships between different variables in the data"
            />
            {insights.correlations && insights.correlations.length > 0 ? (
              insights.correlations.map((corr, idx) => (
                <InsightCard key={idx} insight={corr} index={idx} />
              ))
            ) : (
              <p className="text-sm text-slate-500">No strong correlations detected.</p>
            )}
          </div>
        );

      case 'anomalies':
        return (
          <div className="space-y-4">
            <SectionHeader
              title="Anomalies & Outliers"
              count={insights.anomalies?.length || 0}
              description="Unusual patterns, outliers, and data quality issues requiring attention"
            />
            {insights.anomalies && insights.anomalies.length > 0 ? (
              insights.anomalies.map((anomaly, idx) => (
                <InsightCard key={idx} insight={anomaly} index={idx} />
              ))
            ) : (
              <p className="text-sm text-slate-500">No anomalies detected.</p>
            )}
          </div>
        );

      case 'temporal':
        return (
          <div className="space-y-4">
            <SectionHeader
              title="Temporal Trends"
              count={insights.temporalTrends?.length || 0}
              description="Trends and patterns over time, including rejection rate changes and performance improvements"
            />
            {insights.temporalTrends && insights.temporalTrends.length > 0 ? (
              insights.temporalTrends.map((trend, idx) => (
                <InsightCard key={idx} insight={trend} index={idx} />
              ))
            ) : (
              <p className="text-sm text-slate-500">No temporal trends detected.</p>
            )}
          </div>
        );

      case 'welder':
        const hasWelderData = 
          (insights.welderPerformance?.highRiskWelders && insights.welderPerformance.highRiskWelders.length > 0) ||
          (insights.welderPerformance?.consistentPerformers && insights.welderPerformance.consistentPerformers.length > 0) ||
          (insights.welderPerformance?.welderDefectPatterns && insights.welderPerformance.welderDefectPatterns.length > 0) ||
          (insights.welderPerformance?.welderCombinations && insights.welderPerformance.welderCombinations.length > 0);
        
        const toggleWelderSection = (section: string) => {
          setExpandedWelderSections(prev => {
            const next = new Set(prev);
            if (next.has(section)) {
              next.delete(section);
            } else {
              next.add(section);
            }
            return next;
          });
        };
        
        const CollapsibleSection = ({ 
          sectionKey, 
          title, 
          count, 
          children, 
          defaultExpanded = false 
        }: { 
          sectionKey: string; 
          title: string; 
          count: number; 
          children: React.ReactNode;
          defaultExpanded?: boolean;
        }) => {
          const isExpanded = expandedWelderSections.has(sectionKey);
          
          return (
            <div className="rounded-lg border border-slate-200 bg-white">
              <button
                onClick={() => toggleWelderSection(sectionKey)}
                className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-slate-900">{title}</span>
                  <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                    {count}
                  </span>
                </div>
                <span className="text-slate-400 text-lg">
                  {isExpanded ? '−' : '+'}
                </span>
              </button>
              {isExpanded && (
                <div className="border-t border-slate-200 p-4">
                  {children}
                </div>
              )}
            </div>
          );
        };
        
        return (
          <div className="space-y-4">
            <SectionHeader
              title="Welder Performance Analysis"
              description="Individual welder performance, risk factors, and improvement opportunities"
            />
            
            {!hasWelderData && (
              <p className="text-sm text-slate-500">No welder performance data available. This may be because the dataset doesn't have enough welder information or doesn't meet the analysis thresholds.</p>
            )}
            
            {insights.welderPerformance?.highRiskWelders && insights.welderPerformance.highRiskWelders.length > 0 && (
              <CollapsibleSection
                sectionKey="highRisk"
                title="High-Risk Welders"
                count={insights.welderPerformance.highRiskWelders.length}
                defaultExpanded={true}
              >
                <div className="space-y-3">
                  {insights.welderPerformance.highRiskWelders.map((welder: any, idx: number) => (
                    <div key={idx} className="rounded-lg border bg-red-50 p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-slate-900">Welder {welder.welderNumber}</p>
                          <p className="text-sm text-slate-600">
                            {welder.rejectionRate.toFixed(1)}% rejection rate
                            {welder.overallRate !== null && welder.overallRate !== undefined && (
                              <> ({welder.multiplier.toFixed(1)}x average)</>
                            )}
                          </p>
                          <p className="text-xs text-slate-500">
                            {welder.rejectedCount} rejected out of {welder.totalWelds} welds
                          </p>
                          <p className="text-xs text-slate-500">
                            Primary defect: {welder.primaryDefectType}
                          </p>
                        </div>
                        <SeverityBadge severity="high" />
                      </div>
                    </div>
                  ))}
                </div>
              </CollapsibleSection>
            )}

            {insights.welderPerformance?.consistentPerformers && insights.welderPerformance.consistentPerformers.length > 0 && (
              <CollapsibleSection
                sectionKey="consistentPerformers"
                title="Consistent Performers"
                count={insights.welderPerformance.consistentPerformers.length}
              >
                <div className="space-y-3">
                  {insights.welderPerformance.consistentPerformers.map((welder: any, idx: number) => (
                    <div key={idx} className="rounded-lg border bg-emerald-50 p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-slate-900">Welder {welder.welderNumber}</p>
                          <p className="text-sm text-slate-600">
                            {welder.acceptanceRate.toFixed(1)}% acceptance rate ({welder.rejectionRate.toFixed(1)}% rejection)
                          </p>
                          <p className="text-xs text-slate-500">
                            {welder.totalWelds} total welds
                          </p>
                        </div>
                        <SeverityBadge severity="low" />
                      </div>
                    </div>
                  ))}
                </div>
              </CollapsibleSection>
            )}

            {insights.welderPerformance?.welderDefectPatterns && insights.welderPerformance.welderDefectPatterns.length > 0 && (
              <CollapsibleSection
                sectionKey="defectPatterns"
                title="Welder-Defect Patterns"
                count={insights.welderPerformance.welderDefectPatterns.length}
              >
                <div className="space-y-3">
                  {insights.welderPerformance.welderDefectPatterns.map((pattern: any, idx: number) => (
                    <div key={idx} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                      <p className="font-semibold text-slate-900">Welder {pattern.welderNumber}</p>
                      <p className="text-sm text-slate-600">
                        {pattern.percentage.toFixed(1)}% of defects are {pattern.defectType} ({pattern.count} occurrences)
                      </p>
                      <p className="text-xs text-slate-500 mt-1">{pattern.description}</p>
                    </div>
                  ))}
                </div>
              </CollapsibleSection>
            )}

            {insights.welderPerformance?.welderCombinations && insights.welderPerformance.welderCombinations.length > 0 && (
              <CollapsibleSection
                sectionKey="combinations"
                title="Welder-Repair Welder Combinations"
                count={insights.welderPerformance.welderCombinations.length}
              >
                <div className="space-y-3">
                  {insights.welderPerformance.welderCombinations.map((combo: any, idx: number) => (
                    <div key={idx} className="rounded-lg border bg-orange-50 p-4">
                      <p className="font-semibold text-slate-900">
                        Welder {combo.welderNumber} → Repair by Welder {combo.repairWelderNumber}
                      </p>
                      <p className="text-sm text-slate-600">
                        {combo.rejectionRate.toFixed(1)}% rejection rate
                        {combo.overallRate !== null && combo.overallRate !== undefined && (
                          <> ({combo.multiplier.toFixed(1)}x average)</>
                        )}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">{combo.description}</p>
                    </div>
                  ))}
                </div>
              </CollapsibleSection>
            )}
          </div>
        );

      case 'rootcause':
        return (
          <div className="space-y-6">
            <SectionHeader
              title="Root Cause Analysis"
              count={insights.rootCauseAnalysis?.insights?.length || 0}
              description="Detailed analysis of root causes, structured factors, and defect correlations"
            />
            
            {/* Root Cause Insights */}
            {insights.rootCauseAnalysis?.insights && insights.rootCauseAnalysis.insights.length > 0 && (
              <div>
                <h3 className="mb-3 text-sm font-semibold text-slate-900">Key Root Cause Insights</h3>
                <div className="space-y-3">
                  {insights.rootCauseAnalysis.insights.map((insight: Insight, idx: number) => (
                    <InsightCard key={idx} insight={insight} index={idx} />
                  ))}
                </div>
              </div>
            )}

            {/* Structured Factors */}
            {insights.rootCauseAnalysis?.structuredFactors && insights.rootCauseAnalysis.structuredFactors.length > 0 && (
              <div>
                <h3 className="mb-3 text-sm font-semibold text-slate-900">Root Cause Categories</h3>
                <div className="grid gap-4 md:grid-cols-2">
                  {insights.rootCauseAnalysis.structuredFactors.map((factor: any, idx: number) => (
                    <div key={idx} className="rounded-lg border border-slate-200 bg-white p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold text-slate-900">{factor.category}</h4>
                        <span className="text-sm font-medium text-slate-600">
                          {factor.percentage}%
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mb-2">
                        {factor.frequency} occurrences
                      </p>
                      {factor.subcategories && factor.subcategories.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {factor.subcategories.map((subcat: any, sidx: number) => (
                            <div key={sidx} className="flex items-center justify-between text-xs">
                              <span className="text-slate-600">{subcat.name}</span>
                              <span className="font-medium text-slate-900">{subcat.count}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Root Cause Chains */}
            {insights.rootCauseAnalysis?.rootCauseChains && insights.rootCauseAnalysis.rootCauseChains.length > 0 && (
              <div>
                <h3 className="mb-3 text-sm font-semibold text-slate-900">Root Cause Chains</h3>
                <p className="mb-3 text-xs text-slate-500">
                  Common cause-and-effect relationships identified in root cause analyses
                </p>
                <div className="space-y-3">
                  {insights.rootCauseAnalysis.rootCauseChains.map((chain: any, idx: number) => (
                    <div key={idx} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-slate-900">{chain.primaryCause}</p>
                          <p className="text-xs text-slate-500 mt-1">→ {chain.secondaryCause}</p>
                        </div>
                        <span className="text-sm font-semibold text-slate-700">
                          {chain.frequency}x
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Defect-Root Cause Correlations */}
            {insights.rootCauseAnalysis?.defectRootCauseCorrelations && insights.rootCauseAnalysis.defectRootCauseCorrelations.length > 0 && (
              <div>
                <h3 className="mb-3 text-sm font-semibold text-slate-900">Defect Type vs Root Cause Correlations</h3>
                <p className="mb-3 text-xs text-slate-500">
                  Strong correlations between specific defect types and root cause factors
                </p>
                <div className="space-y-3">
                  {insights.rootCauseAnalysis.defectRootCauseCorrelations.map((corr: any, idx: number) => (
                    <div key={idx} className="rounded-lg border bg-blue-50 p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="font-semibold text-slate-900">{corr.defectType}</p>
                          <p className="text-sm text-slate-600">{corr.rootCauseFactor}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-blue-700">
                            {corr.correlationPercentage}%
                          </p>
                          <p className="text-xs text-slate-500">
                            {corr.occurrences} of {corr.totalDefectCount}
                          </p>
                        </div>
                      </div>
                      <p className="text-xs text-slate-600 mt-2">
                        {corr.rootCauseFactor} appears in {corr.correlationPercentage}% of {corr.defectType} defects
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* RCA Code Frequency */}
            {insights.rootCauseAnalysis?.rcaFrequency && Object.keys(insights.rootCauseAnalysis.rcaFrequency).length > 0 && (
              <div>
                <h3 className="mb-3 text-sm font-semibold text-slate-900">RCA Code Frequency</h3>
                <div className="grid gap-2 md:grid-cols-3">
                  {Object.entries(insights.rootCauseAnalysis.rcaFrequency)
                    .sort((a, b) => (b[1] as number) - (a[1] as number))
                    .slice(0, 9)
                    .map(([code, count], idx) => (
                      <div key={idx} className="rounded-lg border border-slate-200 bg-white p-3">
                        <p className="text-xs font-medium text-slate-600">{code}</p>
                        <p className="text-lg font-bold text-slate-900">{count as number}</p>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        );

      case 'comparative':
        return (
          <div className="space-y-4">
            <SectionHeader
              title="Comparative Analysis"
              count={insights.comparativeAnalysis?.length || 0}
              description="Comparisons across projects, sub-projects, and best practices identification"
            />
            {insights.comparativeAnalysis && insights.comparativeAnalysis.length > 0 ? (
              insights.comparativeAnalysis.map((comp, idx) => (
                <InsightCard key={idx} insight={comp} index={idx} />
              ))
            ) : (
              <p className="text-sm text-slate-500">No comparative insights available.</p>
            )}
          </div>
        );

      case 'recommendations':
        return (
          <div className="space-y-4">
            <SectionHeader
              title="Actionable Recommendations"
              count={insights.recommendations?.length || 0}
              description="Prioritized recommendations based on detected patterns and insights"
            />
            {insights.recommendations && insights.recommendations.length > 0 ? (
              insights.recommendations.map((rec, idx) => (
                <InsightCard key={idx} insight={rec} index={idx} />
              ))
            ) : (
              <p className="text-sm text-slate-500">No recommendations available.</p>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {!hideNav ? (
        <InsightsSectionNav
          aria-label="NDT insights sections"
          tabs={sections.map((s) => ({
            id: s.id,
            label: s.label,
            count: s.count,
          }))}
          activeId={activeSection}
          onChange={setActiveSection}
        />
      ) : null}

      <div>{renderSectionContent()}</div>
    </div>
  );
};

